import { useCallback } from 'react';
import {
  useSetEntityOperationLoadingMsg,
  useAddConfigMessage,
  useEntityOperationError,
} from '@web-config-app/core-react';
import type { ConfigApiError } from '@web-config-app/core';
import type { OnSuccessCustomOperation } from '@web-config-app/core-react';
import { useIntl } from '@leagueplatform/locales';
import { handleEventMessageResponse } from '../../utils/handle-event-message-response';

/**
 * Defines all the event handlers used by custom actions and passed
 * to the react-query useMutation hook.
 */

export const useCustomActionEventHandlers = () => {
  const addConfigMessage = useAddConfigMessage();
  const setEntityOperationLoadingMsg = useSetEntityOperationLoadingMsg();
  const handleEntityOperationError = useEntityOperationError();
  const { formatMessage } = useIntl();

  const createCustomActionEventHandlers = useCallback(
    (setEntityData: (data: any, path?: string) => void) => {
      const onMutate = (loadingMessage?: string) => {
        if (loadingMessage) {
          setEntityOperationLoadingMsg(loadingMessage);
        }
      };

      const onSuccess = ({
        successMessage,
        apiResponse,
        overrideEntityData,
      }: OnSuccessCustomOperation) => {
        if (overrideEntityData && overrideEntityData.shouldOverride) {
          setEntityData(
            apiResponse?.data?.attributes,
            overrideEntityData.path ?? 'attributes.domainEntityAttributes',
          );
        }
        // for custom operation, validation is not required, so we default to success
        handleEventMessageResponse({
          eventMessage: apiResponse?.meta?.eventMessage ?? {
            status: 'success',
          },
          addConfigMessage,
          successMessage,
          formatMessage,
        });
      };

      const onError = (error: ConfigApiError) => {
        handleEntityOperationError(error);
      };

      return {
        onMutate,
        onSuccess,
        onError,
      };
    },
    [
      addConfigMessage,
      setEntityOperationLoadingMsg,
      handleEntityOperationError,
      formatMessage,
    ],
  );

  return createCustomActionEventHandlers;
};
